import WebsiteBehaviors from "../website-behaviors/index.js";
import Citation from "../citation.js";
import {waitUntil} from "../utils/async.js";

export const PN_EEBOOKS_DETAILS_REQUEST = "PN_EEBOOKS_DETAILS_REQUEST";
export const PN_EEBOOKS_DETAILS_RESPONSE = "PN_EEBOOKS_DETAILS_RESPONSE";

const FRAME_TOP = "top";
const FRAME_ACCESSIBLE_VIEWPORT = "accessibleViewport";
const FRAME_EPUB = "epub";

class EbscoEbooks extends WebsiteBehaviors {
	identifier() {
		return "EbscoEbooks";
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'location' implicitly has an 'any' type.
	static test(location) {
		if (EbscoEbooks.isEbscoHost(location)) {
			if (location.pathname.match(/\/ImportWidget\//))
				return false; // Not sure why this is excluded

			// top-level frame for "web" ebook reader
			// like https://proxy.library.upenn.edu/login?url=https://search.ebscohost.com/login.aspx?direct=true&db=nlebk&AN=1810881&site=ehost-live&ebv=EK&ppid=Page-__-1
			if (location.pathname.match(/\/ehost\/ebookviewer\/ebook/))
				return true;

			// top-level frame for "eds" ebook reader
			// like http://search.ebscohost.com.proxy.library.upenn.edu/login.aspx?direct=true&db=nlebk&AN=1463586&site=eds-live&ebv=EK&ppid=Page-__-1
			if (location.pathname.match(/\/eds\/ebookviewer\/ebook/))
				return true;

			// mid-level iframe for both ebook readers
			if (location.pathname.match(/\/EbscoViewerService\//))
				return true;
		}

		return false;
	}

	/**
	 * @param {Url} location
	 * @return boolean
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'location' implicitly has an 'any' type.
	static isEbscoHost(location) {
		// Proxied urls that are loaded on an account with a known proxy config
		// should end up like ....ebscohost.com/, but if you use an account
		// without a known proxy config we might see a proxied url more like
		// ...-ebscohost-com. Either way we should try to function properly.

		return !!(
			location.hostname.match(/\.ebscohost\.com/)
			|| location.hostname.match(/-ebscohost-com/)
		);
	}

	styles() {
		// language=CSS
		return `
			#pdfViewer .textLayer, #epub-viewer-content {
				-webkit-touch-callout: text !important;
				-webkit-user-select: text !important;
				-khtml-user-select: text !important;
				-moz-user-select: text !important;
				-ms-user-select: text !important;
				user-select: text !important;
			}
			#pdfViewer .textLayer .aa-highlight {
				font-size: unset;
			}
		`;
	}

	shouldFrameTakeControl() {
		let yes = false;
		try {
			// Some of this is redundant, but I left it this way
			// so it's easier to see what happens in each frame.
			switch (this.identifyFrame()) {
				case FRAME_TOP:
					yes = false;
					break;

				case FRAME_ACCESSIBLE_VIEWPORT:
					yes = true;

					if (document.getElementById("epub-frame")) {
						// This iframe will, or has already, taken control (the iframe doesn't exist at first; see PN_EBSCO_DESTROY code below).
						yes = false;
					}

					// We need to manually pass messages down to the child frame.
					// (This is used when the permalink changes.)
					window.addEventListener("message", async (evt) => {
						if (typeof evt.data.type === "undefined")
							return;

						switch (evt.data.type) {
							case "FRAME_REPLACE_TOP_URL":
								try {
									const epubFrame = document.getElementById("epub-frame");
									if (!epubFrame) {
										// Happens during initialization when the iframe doesn't exist yet in the DOM.
										// It could also be the pdf.js viewer (no inner frame), in which case this could
										// be a page change so let's render the annotations after giving the page some
										// time to render.
										// Example of the pdf.js viewer:
										// https://proxy.library.upenn.edu/login?url=https://search.ebscohost.com/login.aspx?direct=true&db=nlebk&AN=685876&site=ehost-live&ebv=EB&ppid=pp__C3_BE_C3_BF83
										setTimeout(() => (this as any).hooks.renderAnnotations(), 1000);
										break;
									}

									const exposed = (epubFrame as any).contentWindow.document;
									exposed.documentElement.setAttribute("data-pn-url", evt.data.pageData.pageUrl);
									exposed.documentElement.setAttribute("data-pn-url-final", 1); // Don't let processOrgProxy rewrite the URL (we trust the permalink given by Ebsco).
								} catch (e) {
									// Don't blow up if something changes.
									console.error("PN: Unexpected result for epub-frame", e);
								}
								break;

							case "PN_EBSCO_DESTROY":
								(this as any).hooks.destroy();
								break;
						}
					});
					break;

				case FRAME_EPUB:
					yes = true;

					window.parent.postMessage({type: "PN_EBSCO_DESTROY"});
					break;
			}
		} catch (e) {
			// Probably in accessibleViewport, but we need to be active in its child iframe.
		}

		return yes;
	}

	destroy() {
		if (!(this as any).initialized)
			return;
		(this as any).initialized = false;

		super.destroy();
	}

	async init() {
		if ((this as any).initialized)
			return;
		(this as any).initialized = true;

		await super.init();

		const frame = this.identifyFrame();
		switch (frame) {
			case FRAME_TOP:
				window.addEventListener("message", async (evt) => {
					if (typeof evt.data.type !== "undefined"
						&& evt.data.type === "PN_EBSCO_GET_PERMALINK"
					) {
						await this.getPermalink();
						// @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
						this.updatePageDataToChildFrames();
					}
				});
				break;

			case FRAME_ACCESSIBLE_VIEWPORT:
				// Signal the top frame to do the "permalink" stuff. We trigger it from here to make sure we don't
				// do it too early, betting that when the extension is initializing in this frame, the permalink
				// will be available in the top frame.
				try {
					// Additionally, we wait until we can no longer see the "loading" message, ...
					await waitUntil(() => !document.querySelector("#evsLoading"));
				} catch (e) {} // ... but only for a few seconds.
				// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
				window.top.postMessage({type: "PN_EBSCO_GET_PERMALINK"}, "*");

				// If top frame sends us citation info, save it
				window.addEventListener("message", async (evt) => {
					if (evt.data.type === PN_EEBOOKS_DETAILS_RESPONSE) {
						document.documentElement.setAttribute("data-pn-ebsco-an", evt.data.an);
						document.documentElement.setAttribute("data-pn-ebsco-dbid", evt.data.dbid);
					}
				});
				// Ask top frame for citation information (even though it may not have it)
				// @ts-expect-error FIXME
				window.top.postMessage({type: PN_EEBOOKS_DETAILS_REQUEST}, "*");

				break;

			case FRAME_EPUB: {
				const observer = new MutationObserver((mutationsList, observer) => {
					const pnstyles = document.getElementById("pn-contentscript-styles");
					if (!pnstyles)
						(this as any).hooks.injectStyles(); // inject standard content-script styles
					// The mutation that triggered this event handler probably was caused by
					// a new chapter being rendered (in which case ebsco wipes the iframe
					// and re-inserts everything). In order to make sure highlights are
					// rendered properly, we'll now force them to re-render, after a delay
					// (in order to ensure that ebsco has time to add the new content to the frame).
					setTimeout(() => (this as any).hooks.renderAnnotations(), 500);
				});

				const config = {
					attributes: true,
					childList:  true,
					subtree:    true
				};

				observer.observe(document.head, config);

				// Inject custom site styles (see styles(), above)
				this._injectStyles();

				window.addEventListener("message", async (evt) => {
					if (typeof evt.data.type !== "undefined"
						&& evt.data.type === "FRAME_REPLACE_TOP_URL"
					) {
						// Page-turn events eventually end up here.
						// The pageData has already been updated,
						// so we need to re-render the annotations now.
						(this as any).hooks.renderAnnotations();

						// We also minimize the sidebar to prevent the issue
						// described below (which also happens on page turns),
						// but only if the "chapter" changes (thus loading new
						// markup, which is what in some cases causes the issue).
						if (evt.data.ebscoCurrentPageId && (this as any).ebscoCurrentPageId !== evt.data.ebscoCurrentPageId) {
							(this as any).ebscoCurrentPageId = evt.data.ebscoCurrentPageId;
							(this as any).hooks.minimizeSidebar();
						}
					}
				});

				// We need to make sure the sidebar is minimized in order
				// to avoid an issue where ebsco writes a bunch of styles
				// directly into our elements, making the sidebar ugly
				// and unusable (until you minimize/expand it again).
				setTimeout(() => (this as any).hooks.minimizeSidebar(), 500);

				break;
			}
		}
	}

	/**
	 * Only called in the top frame.
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'extra' implicitly has an 'any' type.
	updatePageDataToChildFrames(extra) {
		const replaceTopUrlData = {
			type:     "FRAME_REPLACE_TOP_URL",
			pageData: this.getPageData()
		};

		if (extra)
			Object.assign(replaceTopUrlData, extra);

		// Compare to code in content-script/main.js
		// eslint-disable-next-line @typescript-eslint/prefer-for-of
		for (let i = 0; i < window.frames.length; i++)
			window.frames[i].postMessage(replaceTopUrlData, "*");
	}

	async getPermalink() {
		// First need to click on the permalink button to enable whatever updates the permalink
		const event = new MouseEvent("click", {
			view:       window,
			bubbles:    true,
			cancelable: true
		});
		// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
		document.querySelector("a.permalink-link").dispatchEvent(event);

		// Now wait for the link to show up
		const stopCondition = () => {
			const plink = document.getElementById("pLinkDetail");
			return (plink !== null && (plink as any).value !== "");
		};

		try {
			await waitUntil(stopCondition);

			// Close the toolbar; the permalink element remains in the dom and is updated as the user navigates.
			// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
			document.getElementById("ToolPanelContent").querySelector("a.close-panel").dispatchEvent(event);
		} catch (e) {
			// Just move on
			console.error("PN: Gave up waiting for permalink", e);
		}
	}

	async topFrameHandler() {
		window.addEventListener("message", async (evt) => {
			// Try to get citation info from top frame script text
			if (evt.data.type === PN_EEBOOKS_DETAILS_REQUEST) {
				const scripts = window.parent.document.querySelectorAll("script");
				if (scripts) {
					const {an, dbid} = this.getCitationInputs(scripts);

					// Only respond to request if the information exists
					if (an && dbid) {
						// @ts-expect-error FIXME
						evt.source.postMessage({
							type: PN_EEBOOKS_DETAILS_RESPONSE,
							an,
							dbid
						// @ts-expect-error FIXME
						}, "*");
					}
				}
				return;
			}

			// Monitor for page changes so we can update our link data when it happens
			try {
				const data = JSON.parse(evt.data); // EBSCO is passing json-encoded data between frames...
				if (data.theSignature === "pageTurn.ebookViewer") {
					// Page-turn event. Need to update the permalink info we're using.
					// This doesn't update it in the top-level frame, but I think it
					// only really matters in the frame that's running the highlighting/sidebar anyway.

					let extra = null;
					// `artifactId` stores part of the url for the current page. We strip the fragment
					// and return the rest, so that the sidebar is minimized but only if we're changing
					// the "chapter", not just the "page".
					if (data.theArgs?.artifactId)
						extra = {ebscoCurrentPageId: data.theArgs.artifactId.replace(/#.*$/, "")};
					this.updatePageDataToChildFrames(extra);
				}
			} catch (e) {
				// ignore
			}
		});

		return null;
	}

	getPageData() {
		let pageUrl = window.location.href; // If the stuff below fails, let's at least get the window URL
		const plinkElement = document.getElementById("pLinkDetail");
		if (plinkElement)
			pageUrl = (plinkElement as any).value;
		const pageTitle = document.title;

		return {
			pageTitle,
			pageUrl
		};
	}

	identifyFrame() {
		let frame = null;
		try {
			if (window.top === window.self)
				frame = FRAME_TOP; // UI at top of page
			else if (window.name === "accessibleViewport")
				frame = FRAME_ACCESSIBLE_VIEWPORT; // book UI on left of page
			// @ts-expect-error FIXME
			else if (window.parent.name === "accessibleViewport" || window.frameElement.id === "epub-frame")
				// in Ebsco Discovery Ebooks, the only identifying thing is the frame element id (which is the same as normal ebsco ebooks)
				frame = FRAME_EPUB; // frame actually rendering content
		} catch (e) {}

		return frame;
	}

	// Given a NodeList of script elements, finds the one that contains citation
	// information (an and dbid for EBSCO citation API call).
	// If none are found, returns an empty array.
	// @ts-expect-error FIXME
	getCitationInputs(scripts) {
		const script = Array.from(scripts).find(script => {
			// @ts-expect-error FIXME
			return script.textContent.includes("currentRecord");
		});

		if (!script)
			return {};

		// Look for something like: "currentRecord":{"Db":"nlebk","Tag":"AN","Term":1463586}
		let regexp;
		try {
			regexp = new RegExp('(?<="(currentRecord)":)\\S*?}', "g");
		} catch (e) {
			// 2023-01-27: Safari doesn't support this yet: https://caniuse.com/js-regexp-lookbehind
			return {};
		}

		// @ts-expect-error FIXME
		const text = script.textContent.match(regexp)[0];
		const scriptJson = JSON.parse(text);

		const an = scriptJson.Term;
		const dbid = scriptJson.Db;

		return {an, dbid};
	}

	// @ts-expect-error FIXME
	async generateStructuredCitation(url, getEbscoCitationRis) {
		// See if these values were given to us by the top frame
		let an = document.documentElement.getAttribute("data-pn-ebsco-an");
		let dbid = document.documentElement.getAttribute("data-pn-ebsco-dbid");

		// Try to get an and dbid from DOM script text if it wasn't given to us
		// by the top frame
		if (!an || !dbid) {
			const scripts = window.parent.document.querySelectorAll("script");
			({an, dbid} = this.getCitationInputs(scripts));
		}

		const ris = await getEbscoCitationRis(an, dbid);

		return Citation.fromRIS(ris);
	}
}

export default EbscoEbooks;
